export default function Home() {
  return (
    <main>
      {/* ================================================================
          SECTION 1: HERO — Full viewport, dark abstract background
          Company identifier appears HERE ONLY, at the top-left
          ================================================================ */}
      <section className="relative h-screen min-h-[700px] flex flex-col justify-center overflow-hidden">
        {/* Background image with dark overlay */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20abstract%20architectural%20geometric%20composition%20with%20intersecting%20smooth%20planes%20and%20subtle%20gradient%20depth%2C%20monochromatic%20deep%20charcoal%20and%20graphite%20tones%2C%20soft%20diffused%20ambient%20lighting%20creating%20gentle%20highlights%20on%20edges%2C%20minimalist%20modern%20aesthetic%20with%20serene%20atmosphere%2C%20no%20people%20no%20text%20no%20products%2C%20clean%20editorial%20photography%20style%20with%20smooth%20matte%20surfaces&width=1920&height=1080&seq=klava-hero-bg-2026&orientation=landscape"
            alt="Abstract architectural geometric texture"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/30"></div>
        </div>

        {/* Company identifier — the ONLY place (KLAVA CODE, MB) appears */}
        <div className="absolute top-0 left-0 right-0 z-20 px-8 md:px-14 pt-8 md:pt-10">
          <p className="text-xs md:text-sm font-light tracking-[0.28em] uppercase text-white/80 whitespace-nowrap leading-relaxed">
            (KLAVA CODE, MB)
          </p>
        </div>

        {/* Main content zone — left-right split */}
        <div className="relative z-10 w-full px-8 md:px-14 flex flex-col lg:flex-row lg:items-end gap-12 lg:gap-20 mt-20 md:mt-24">
          {/* Left column — massive heading */}
          <div className="lg:w-3/5 animate-fade-up">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light text-white leading-[1.05] tracking-tight">
              <span className="block">Modern iOS</span>
              <span className="block">Platform</span>
              <span className="block">Engineering</span>
            </h1>
          </div>

          {/* Right column — description */}
          <div className="lg:w-2/5 pb-4 animate-fade-up animate-delay-200">
            <p className="text-sm md:text-base font-light text-white/75 leading-relaxed max-w-md">
              A professional engineering team focused on designing and developing modern digital software solutions for the iOS platform and related technologies. We build scalable, maintainable, and high-performance systems with a strong focus on usability, stability, and long-term technical quality.
            </p>
          </div>
        </div>

        {/* Subtle bottom fade indicator — no interaction, purely visual */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-5 h-9 rounded-full border border-white/20 flex items-start justify-center pt-2">
            <div className="w-[1px] h-2.5 bg-white/30 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 2: PHILOSOPHY — Light background, centered editorial
          ================================================================ */}
      <section className="relative py-28 md:py-36 px-6 md:px-14 bg-background-50">
        <div className="max-w-3xl mx-auto text-center">
          {/* Capsule label */}
          <div className="inline-block px-4 py-1.5 border border-secondary-300/60 rounded-full mb-12 animate-fade-in animate-delay-100">
            <span className="text-xs font-medium tracking-[0.15em] uppercase text-secondary-600">Our Approach</span>
          </div>

          <p className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 leading-relaxed animate-fade-up animate-delay-200">
            We follow modern software engineering practices — clean architecture,
            performance optimization, and user-centered design. Every system we
            create is built to evolve with the technology landscape while
            maintaining stability and reliability.
          </p>

          <p className="mt-8 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Continuous innovation and improvement of development workflows are at
            the core of how we work. We actively explore new technologies and
            evolve our internal processes to maintain high technical excellence,
            ensuring that every solution we deliver is future-ready and built
            to the highest standards of engineering quality.
          </p>
        </div>
      </section>

      {/* ================================================================
          SECTION 3: CORE CAPABILITIES — Three-card grid
          ================================================================ */}
      <section className="relative py-28 md:py-36 px-6 md:px-14 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-16">
            Core Capabilities
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {/* Card 1 — iOS Platform */}
            <div className="bg-background-50 rounded-2xl p-8 md:p-10 flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-accent-100/80 flex items-center justify-center mb-8">
                <i className="ri-smartphone-line text-accent-600 text-xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground-950 mb-4">
                iOS Platform Engineering
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Designing robust native solutions for the iOS ecosystem with deep
                understanding of platform conventions, performance characteristics,
                and user experience patterns. Every component is crafted to feel
                natural and responsive within its environment.
              </p>
            </div>

            {/* Card 2 — Scalable Systems */}
            <div className="bg-foreground-950 rounded-2xl p-8 md:p-10 flex flex-col relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.06]">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20neutral%20architectural%20texture%20with%20layered%20geometric%20patterns%20and%20subtle%20depth%20of%20field%2C%20warm%20beige%20and%20charcoal%20tones%20blending%20softly%2C%20smooth%20surfaces%20with%20gentle%20shadows%2C%20minimalist%20modern%20aesthetic%2C%20no%20people%20no%20text%20no%20products%2C%20clean%20editorial%20photography%20style&width=800&height=600&seq=klava-card-bg-2026&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 mb-8">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
                  <span className="text-xs font-medium text-white/70 uppercase tracking-wider">Architecture</span>
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-4">
                  Scalable Systems Architecture
                </h3>
                <p className="text-sm md:text-base text-white/65 leading-relaxed">
                  Building maintainable architectures that grow with evolving
                  requirements, ensuring long-term technical health and codebase
                  quality. Our approach prioritizes modularity, separation of
                  concerns, and clean dependency management.
                </p>
              </div>
            </div>

            {/* Card 3 — Performance & Stability */}
            <div className="bg-background-50 rounded-2xl p-8 md:p-10 flex flex-col">
              <div className="w-12 h-12 rounded-xl bg-primary-100/80 flex items-center justify-center mb-8">
                <i className="ri-speed-up-line text-primary-600 text-xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground-950 mb-4">
                Performance &amp; Stability
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Optimizing every layer of the stack for responsiveness,
                reliability, and seamless user experiences across all conditions.
                We treat performance as a first-class engineering concern, not an
                afterthought.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 4: ENGINEERING STANDARDS — Alternating background, 2x2
          ================================================================ */}
      <section className="relative py-28 md:py-36 px-6 md:px-14 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-16">
            Engineering Standards
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
            {/* Standard 1 */}
            <div className="flex gap-5">
              <div className="w-1.5 flex-shrink-0 rounded-full bg-accent-400 mt-1.5 self-stretch"></div>
              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">
                  Clean Architecture
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Separation of concerns, testable components, and modular design
                  principles form the foundation of every system we build.
                  Clear boundaries between layers ensure long-term maintainability
                  and reduce technical debt.
                </p>
              </div>
            </div>

            {/* Standard 2 */}
            <div className="flex gap-5">
              <div className="w-1.5 flex-shrink-0 rounded-full bg-primary-400 mt-1.5 self-stretch"></div>
              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">
                  Performance Optimization
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Efficient resource usage, smooth rendering, and fast load times
                  are engineered into every component from the start. We profile
                  early, measure continuously, and optimize with precision.
                </p>
              </div>
            </div>

            {/* Standard 3 */}
            <div className="flex gap-5">
              <div className="w-1.5 flex-shrink-0 rounded-full bg-secondary-400 mt-1.5 self-stretch"></div>
              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">
                  User-Centered Design
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Intuitive interfaces built around real user needs and behaviors.
                  Every interaction is designed with purpose, clarity, and
                  accessibility in mind, creating experiences that feel effortless.
                </p>
              </div>
            </div>

            {/* Standard 4 */}
            <div className="flex gap-5">
              <div className="w-1.5 flex-shrink-0 rounded-full bg-accent-400 mt-1.5 self-stretch"></div>
              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">
                  Continuous Improvement
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  Regular refinement of development workflows, adoption of modern
                  engineering standards, and proactive technical evolution keep
                  our output consistently excellent and our team continuously
                  learning and growing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 5: VALUES — What drives the team
          ================================================================ */}
      <section className="relative py-28 md:py-36 px-6 md:px-14 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-16">
            What Drives Us
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {/* Value 1 */}
            <div>
              <div className="w-10 h-10 rounded-full bg-accent-100/80 flex items-center justify-center mb-6">
                <span className="text-accent-600 text-sm font-bold">01</span>
              </div>
              <h3 className="text-xl font-heading text-foreground-950 mb-4">
                Technical Excellence
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                We hold our code to the highest standards of quality and
                reliability. Rigorous testing, thoughtful design, and meticulous
                attention to detail define every deliverable that leaves our team.
              </p>
            </div>

            {/* Value 2 */}
            <div>
              <div className="w-10 h-10 rounded-full bg-primary-100/80 flex items-center justify-center mb-6">
                <span className="text-primary-600 text-sm font-bold">02</span>
              </div>
              <h3 className="text-xl font-heading text-foreground-950 mb-4">
                Innovation
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                We actively explore emerging technologies and evolve our processes
                to stay ahead. Innovation is not a buzzword for us — it is a
                daily practice embedded in how we approach every engineering
                challenge.
              </p>
            </div>

            {/* Value 3 */}
            <div>
              <div className="w-10 h-10 rounded-full bg-secondary-200/80 flex items-center justify-center mb-6">
                <span className="text-secondary-700 text-sm font-bold">03</span>
              </div>
              <h3 className="text-xl font-heading text-foreground-950 mb-4">
                Long-Term Thinking
              </h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Every architectural decision is made with future maintainability
                and scalability in mind. We build systems designed to serve for
                years, not months, anticipating growth and change from day one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SECTION 6: CONTACT — Text only, no form, no interactive elements
          ================================================================ */}
      <section className="relative py-28 md:py-36 px-6 md:px-14 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-16">
            Contact
          </h2>

          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
            {/* Left — complete address block */}
            <div className="lg:w-2/5">
              <p className="text-base text-foreground-500 mb-8 leading-relaxed">
                Based in Kaunas, Lithuania, we work with clients who value
                technical precision and long-term engineering quality.
              </p>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium tracking-[0.12em] uppercase text-foreground-400 mb-1">Address</p>
                  <p className="text-base text-foreground-800">Kurpiu g. 17-6</p>
                </div>
                <div className="flex gap-12">
                  <div>
                    <p className="text-xs font-medium tracking-[0.12em] uppercase text-foreground-400 mb-1">City</p>
                    <p className="text-base text-foreground-800">Kaunas</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-[0.12em] uppercase text-foreground-400 mb-1">Postal Code</p>
                    <p className="text-base text-foreground-800">44287</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium tracking-[0.12em] uppercase text-foreground-400 mb-1">Country</p>
                  <p className="text-base text-foreground-800">Lithuania</p>
                </div>
              </div>
            </div>

            {/* Right — three-column contact details */}
            <div className="lg:w-3/5 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10">
              <div>
                <p className="text-xs font-medium tracking-[0.12em] uppercase text-foreground-400 mb-3">Location</p>
                <p className="text-base text-foreground-800 leading-relaxed">
                  Kurpiu g. 17-6<br />
                  Kaunas, 44287<br />
                  Lithuania
                </p>
              </div>

              <div>
                <p className="text-xs font-medium tracking-[0.12em] uppercase text-foreground-400 mb-3">Phone</p>
                <p className="text-base text-foreground-800">
                  +370 66806274
                </p>
              </div>

              <div>
                <p className="text-xs font-medium tracking-[0.12em] uppercase text-foreground-400 mb-3">Email</p>
                <p className="text-base text-foreground-800">
                  contact@klavacode.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          FOOTER — Minimal closing
          ================================================================ */}
      <footer className="relative py-10 px-6 md:px-14 bg-background-100 border-t border-background-200/70">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
}